import {
  Box,
  Heading,
  Text,
  Button,
  Flex,
  Spinner,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from "@chakra-ui/react";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useAdmin } from "../../context/AdminContext";

export default function AdminMessages() {
  const colors = {
    teal: "#1da179",
    tealLight: "#34c49b",
    grayBg: "#f9f9f9",
  };

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const cancelRef = useRef();

  const navigate = useNavigate();
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const { isAdmin } = useAdmin();

  useEffect(() => {
    if (!isAdmin) {
      navigate("/admin/login");
    }
  }, [isAdmin, navigate]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Token missing");

      const res = await axios.get("https://actpolymers-spring-26d90d713ec5.herokuapp.com/api/messages", {
        headers: { Authorization: `Bearer ${token}` },
      });

      setMessages(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
      toast({
        title: isArabic ? "فشل تحميل الرسائل" : "Failed to load messages",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteAllMessages = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete("https://actpolymers-spring-26d90d713ec5.herokuapp.com/api/messages", {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({
        title: isArabic
          ? "تم حذف جميع الرسائل"
          : "All messages have been deleted",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setMessages([]);
    } catch (error) {
      toast({
        title: isArabic ? "فشل الحذف" : "Failed to delete messages",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchMessages();
    }
  }, [isAdmin]);

  if (!isAdmin) return null;

  if (loading) {
    return (
      <Box
        py={20}
        px={6}
        minH="60vh"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Spinner size="xl" color={colors.teal} />
      </Box>
    );
  }

  // Delete confirmation dialog
  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);
  const confirmDelete = async () => {
    closeDialog();
    await deleteAllMessages();
  };

  return (
    <Box py={10} px={6} bg="white" borderRadius="xl" boxShadow="md">
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        mb={6}
        gap={4}
      >
        <Heading size="lg" color={colors.teal}>
          {isArabic ? "إدارة الرسائل" : "Messages Management"}
        </Heading>

        <Button
          onClick={openDialog}
          bg="red.500"
          color="white"
          _hover={{ bg: "red.600" }}
        >
          {isArabic ? "حذف جميع الرسائل" : "Delete All Messages"}
        </Button>
      </Flex>

      {messages.length === 0 ? (
        <Text textAlign="center" color="gray.500" py={20}>
          {isArabic ? "لا توجد رسائل" : "No messages found."}
        </Text>
      ) : (
        <TableContainer borderRadius="lg" boxShadow="sm" overflowX="auto">
          <Table variant="striped" colorScheme="teal">
            <Thead bg={colors.teal}>
              <Tr>
                <Th color="white">{isArabic ? "الاسم" : "Name"}</Th>
                <Th color="white">{isArabic ? "رقم الجوال" : "Phone"}</Th>
                <Th color="white">{isArabic ? "البريد الإلكتروني" : "Email"}</Th>
                <Th color="white">{isArabic ? "المنتج" : "Product"}</Th>
                <Th color="white">{isArabic ? "اللون / الكود" : "Color Info"}</Th>
                <Th color="white">{isArabic ? "الرسالة" : "Message"}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {messages.map((msg, index) => (
                <Tr key={index}>
                  <Td>{msg.name}</Td>
                  <Td>{msg.phone}</Td>
                  <Td>{msg.email}</Td>
                  <Td>{msg.productName || "-"}</Td>
                  <Td>{msg.colorInfo || "-"}</Td>
                  <Td>{msg.message}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {/* Confirm Delete Dialog */}
      <AlertDialog
        isOpen={isDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {isArabic ? "تأكيد الحذف" : "Confirm Deletion"}
            </AlertDialogHeader>

            <AlertDialogBody>
              {isArabic
                ? "هل أنت متأكد أنك تريد حذف جميع الرسائل؟ هذا الإجراء لا يمكن التراجع عنه."
                : "Are you sure you want to delete all messages? This action cannot be undone."}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeDialog}>
                {isArabic ? "إلغاء" : "Cancel"}
              </Button>
              <Button
                bg="red.500"
                color="white"
                _hover={{ bg: "red.600" }}
                onClick={confirmDelete}
                ml={3}
              >
                {isArabic ? "نعم، حذف" : "Yes, Delete"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
